import type { AudioClip, Clip, Composition, FabricClip, Track, TrackKind } from '../../timeline/types';
import type { AudioLayer, Layer, PieceModel } from '../types';

const KIND_TO_TRACK: Record<Layer['kind'], TrackKind> = {
  text: 'text',
  image: 'media',
  video: 'video',
  shape: 'shape',
  pill: 'text',
  sobreimpreso: 'text',
  group: 'shape',
  svg: 'shape',
  audio: 'audio'
};

const KIND_LABEL: Record<Layer['kind'], string> = {
  text: 'Texto',
  image: 'Imagen',
  video: 'Video',
  shape: 'Forma',
  pill: 'Texto',
  sobreimpreso: 'Texto',
  group: 'Grupo',
  svg: 'SVG',
  audio: 'Audio'
};

const trackOf = (layer: Layer, index: number): Track => ({
  id: `track_${layer.id}`,
  layerId: layer.id,
  kind: KIND_TO_TRACK[layer.kind],
  label: layer.label ?? layer.pieceId ?? `${KIND_LABEL[layer.kind]} ${index + 1}`,
  visible: layer.visible,
  locked: layer.locked
});

const clipOf = (layer: Layer, trackId: string): Clip => {
  if (layer.kind === 'audio') {
    return {
      id: layer.id,
      kind: 'audio',
      trackId,
      start: layer.start,
      duration: layer.duration,
      fit: layer.fit,
      src: layer.content.src,
      ...(layer.content.gain !== undefined ? { gain: layer.content.gain } : {}),
      ...(layer.content.fadeOutMs !== undefined ? { fadeOutMs: layer.content.fadeOutMs } : {}),
      ...(layer.content.trim ? { trim: layer.content.trim } : {}),
      ...(layer.laneKey ? { laneKey: layer.laneKey } : {})
    } satisfies AudioClip;
  }

  return {
    id: layer.id,
    kind: 'fabric',
    trackId,
    objectId: layer.id, // identity = layerId
    start: layer.start,
    duration: layer.duration,
    fit: layer.fit,
    ...(layer.enter ? { enter: layer.enter } : {}),
    ...(layer.exit ? { exit: layer.exit } : {}),
    ...(layer.kind === 'video' ? { intrinsicDuration: layer.content.intrinsicDuration } : {}),
    ...(layer.laneKey ? { laneKey: layer.laneKey } : {}),
    ...(layer.timeAttach ? { timeAttach: layer.timeAttach } : {})
  } satisfies FabricClip;
};

/**
 * Builds the Composition shape from the model so the timeline and playback can read it.
 * Front layers go on top (reverse stack order); audio goes last.
 */
export const modelToComposition = (model: PieceModel): Composition => {
  const tracksById: Record<string, Track> = {};
  const clipsById: Record<string, Clip> = {};
  const trackOrder: string[] = [];

  const visual: Layer[] = [];
  for (let i = model.layerOrder.length - 1; i >= 0; i--) {
    const layer = model.layers[model.layerOrder[i]];
    if (layer) visual.push(layer);
  }
  const audio = Object.values(model.layers).filter((l): l is AudioLayer => l.kind === 'audio');

  [...visual, ...audio].forEach((layer, index) => {
    const track = trackOf(layer, index);
    tracksById[track.id] = track;
    trackOrder.push(track.id);

    const clip = clipOf(layer, track.id);
    clipsById[clip.id] = clip;
  });

  return {
    id: model.id,
    duration: model.duration,
    fps: model.fps,
    trackOrder,
    tracksById,
    clipsById
  };
};
